import { DEEP_GREEN, INCIDENT_ACCENT, INK, MUTED } from '../colorTokens';
import { formatUtcDate, IMPACT_NOTE, MAJOR_DEFINITION, openSummary, PAGE_SCOPE_NOTE, PRIORITY_NOTE, problemNote } from './reportCopy';
import { COLUMNS, days, withUnit } from './ReportSections';

// Builds the PowerPoint for the selected month from the same data the page shows (the scope, the month's
// bullets and the still-open notes), so the file always matches the screen. pptxgenjs is loaded only when
// someone clicks Download, so it stays out of the main bundle.

const hex = (color) => color.replace('#', '');
const GREEN = hex(DEEP_GREEN);
const RED = hex(INCIDENT_ACCENT);
const TEXT = hex(INK);
const GREY = hex(MUTED);
const FONT = 'Calibri';

/** Characters that are illegal in a PowerPoint file. ServiceNow notes sometimes contain them. */
const pptText = (value) => String(value == null ? '' : value)
  // eslint-disable-next-line no-control-regex -- strip illegal XML control characters from ServiceNow text
  .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
  .replace(/\u00A0/g, ' ');

const plainText = (parts) => parts.map((part) => pptText(part.text)).join('');

/** Rich-text runs for pptxgenjs from the { text, bold, color } parts the page uses. */
const runsOf = (parts) => parts.map((part) => ({
  text: pptText(part.text),
  options: { bold: part.bold, color: hex(part.color || INK) }
})).filter((run) => run.text);

const addTitle = (slide, text, subtitle) => {
  slide.addText(pptText(text), { x: 0.5, y: 0.25, w: 12.3, h: 0.5, fontFace: FONT, fontSize: 24, bold: true, color: GREEN });
  if (subtitle) slide.addText(pptText(subtitle), { x: 0.5, y: 0.7, w: 12.3, h: 0.3, fontFace: FONT, fontSize: 12, color: GREY });
};

const addTile = (slide, { x, y, value, label, fill }) => {
  slide.addShape('roundRect', { x, y, w: 1.7, h: 1.0, fill: { color: fill }, rectRadius: 0.1, line: { color: fill } });
  slide.addText(String(value), { x, y: y + 0.08, w: 1.7, h: 0.55, align: 'center', fontFace: FONT, fontSize: 26, bold: true, color: 'FFFFFF' });
  slide.addText(label.toUpperCase(), { x, y: y + 0.62, w: 1.7, h: 0.3, align: 'center', fontFace: FONT, fontSize: 10, bold: true, color: 'FFFFFF' });
};

const noteLine = (note, label) => {
  const line = note.lines.find((item) => item.label === label);
  return line ? line.text : '';
};

const headerCell = (size) => (text) => ({
  text, options: { bold: true, color: 'FFFFFF', fill: { color: GREEN }, fontFace: FONT, fontSize: size + 1, valign: 'middle' }
});

const TABLE_OPTIONS = {
  border: { type: 'solid', pt: 0.5, color: 'DDDDDD' },
  autoPage: true,
  autoPageRepeatHeader: true
};

/**
 * Downloads "Incident-Remediation-<month>.pptx", five slides that follow the page:
 *   cover, the month (summary tiles and bullets), trends, still open: status and next steps, the problem table.
 * active = the month group; scope, averageClosedDays, closedForAverageCount and stillOpenFacts come from buildScope;
 * rows = the table rows for the month. A table that does not fit one slide continues on the next.
 */
export const downloadPresentation = async ({ active, scope, averageClosedDays, closedForAverageCount, stillOpenFacts, rows }) => {
  const { default: PptxGenJS } = await import('pptxgenjs');
  const pres = new PptxGenJS();
  pres.layout = 'LAYOUT_WIDE'; // 13.33 x 7.5 in
  pres.title = `Incident Remediation - ${active.label}`;

  // 1. Cover
  const cover = pres.addSlide();
  cover.background = { color: GREEN };
  cover.addText('Incident Remediation', { x: 0.8, y: 2.4, w: 11.5, h: 1.1, fontFace: FONT, fontSize: 44, bold: true, color: 'FFFFFF' });
  cover.addText(`Monthly report: ${active.label}`, { x: 0.8, y: 3.5, w: 11.5, h: 0.6, fontFace: FONT, fontSize: 22, color: 'FFFFFF' });
  cover.addText(`Generated ${new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`, {
    x: 0.8, y: 4.2, w: 11.5, h: 0.4, fontFace: FONT, fontSize: 14, color: 'CCCCCC'
  });

  // 2. The month
  const summary = pres.addSlide();
  addTitle(summary, active.label, `Incident Remediation: all problems through ${active.shortLabel}`);
  const tiles = [
    { value: scope.total, label: 'Total', fill: GREEN },
    { value: active.closedThisMonth || 0, label: `Closed in ${active.shortLabel.split(' ')[0]}`, fill: GREY },
    { value: scope.majorOpen + scope.nonMajorOpen, label: 'Still open', fill: RED },
    { value: scope.major, label: 'Major', fill: GREEN },
    { value: scope.nonMajor, label: 'Non-major', fill: '2C4A3A' }
  ];
  tiles.forEach((tile, index) => addTile(summary, { x: 0.5 + index * 2.4, y: 1.35, ...tile }));
  summary.addText(pptText(PAGE_SCOPE_NOTE), {
    x: 0.5, y: 2.5, w: 12.3, h: 0.35, valign: 'top', fontFace: FONT, fontSize: 12, color: GREY
  });
  summary.addText(
    pptText(`Closed in ${active.shortLabel.split(' ')[0]} is calendar-month closures. Closed under Major / Non-major is opened in the period and since closed.`),
    { x: 0.5, y: 2.85, w: 12.3, h: 0.35, valign: 'top', fontFace: FONT, fontSize: 11, color: GREY }
  );
  summary.addText(runsOf(openSummary(stillOpenFacts).parts), {
    x: 0.5, y: 3.25, w: 12.3, h: 0.45, valign: 'top', fontFace: FONT, fontSize: 16, color: TEXT
  });
  const bullets = active.points
    .map((point) => pptText(plainText(point.parts)))
    .filter(Boolean)
    .map((text) => ({
      text,
      options: { bullet: true, breakLine: true, fontFace: FONT, fontSize: 16, color: TEXT, paraSpaceAfter: 8 }
    }));
  if (bullets.length) {
    summary.addText(bullets, { x: 0.5, y: 3.8, w: 12.3, h: 3.3, valign: 'top' });
  }

  // 3. Trends
  const trend = pres.addSlide();
  addTitle(trend, 'Trends', 'Problems opened per month');
  const labels = scope.chart.map((row) => row.tick);
  const labelStyle = { showValue: true, dataLabelFontSize: 11, dataLabelFontBold: true, dataLabelFormatCode: '0;;;' };
  trend.addChart([
    {
      type: pres.charts.BAR,
      data: [
        { name: 'Opened that month, later closed', labels, values: scope.chart.map((row) => row.closed) },
        { name: 'Opened that month, still open', labels, values: scope.chart.map((row) => row.openProblems) }
      ],
      options: {
        barDir: 'col', barGrouping: 'stacked', chartColors: [GREY, RED], barGapWidthPct: 60,
        ...labelStyle, dataLabelColor: 'FFFFFF', dataLabelPosition: 'ctr'
      }
    },
    {
      // Invisible line whose labels sit above each bar: the month's total, as on the page
      type: pres.charts.LINE,
      data: [{ name: 'Total opened', labels, values: scope.chart.map((row) => row.opened) }],
      options: {
        chartColors: ['FFFFFF'], lineSize: 1, lineDataSymbol: 'none',
        ...labelStyle, dataLabelColor: TEXT, dataLabelPosition: 't'
      }
    }
  ], {
    x: 0.5, y: 1.2, w: 12.3, h: 4.0, showLegend: true, legendPos: 't', legendFontSize: 12,
    catAxisLabelFontSize: 11, valAxisLabelFontSize: 11, valAxisMajorUnit: 4, valGridLine: { color: 'E5E5E5', size: 0.5 }
  });
  const trendLines = [];
  if (active.closedThisMonth != null) {
    trendLines.push({
      text: `${active.closedThisMonth} problem${active.closedThisMonth === 1 ? ' was' : 's were'} closed in ${active.label}.`,
      options: { breakLine: true, paraSpaceAfter: 6 }
    });
  }
  if (averageClosedDays != null) {
    trendLines.push({
      text: `${averageClosedDays} days is the average time to close, across ${closedForAverageCount} closed problem${closedForAverageCount === 1 ? '' : 's'}.`,
      options: { breakLine: true, paraSpaceAfter: 6 }
    });
  }
  if (scope.trend.parts.length) trendLines.push(...runsOf(scope.trend.parts));
  if (trendLines.length) {
    trend.addText(trendLines, { x: 0.5, y: 5.35, w: 12.3, h: 1.6, valign: 'top', fontFace: FONT, fontSize: 14, color: TEXT });
  }

  // 4. Still open. Long notes wrap, so rows continue on the next slide instead of running off the bottom.
  if (stillOpenFacts.length) {
    const size = 9;
    const cell = (text, options = {}) => ({ text: pptText(text), options: { fontFace: FONT, fontSize: size, color: TEXT, valign: 'top', ...options } });
    const notes = stillOpenFacts.map((fact) => {
      const note = problemNote(fact);
      return {
        number: note.number,
        addressed: noteLine(note, 'Problem being addressed'),
        cause: noteLine(note, 'Root cause or risk'),
        status: noteLine(note, 'Current status'),
        progress: [noteLine(note, 'Progress made'), noteLine(note, 'Expected outcome')].filter(Boolean).join(' '),
        next: noteLine(note, 'Next step')
      };
    });
    const columns = [
      { key: 'number', title: 'Number', width: 0.95, style: { bold: true, color: RED } },
      { key: 'addressed', title: 'Problem being addressed', width: 2.5 },
      { key: 'cause', title: 'Root cause or risk', width: 3.0 },
      { key: 'status', title: 'Current status', width: 2.0 },
      { key: 'progress', title: 'Progress / target', width: 1.8 },
      { key: 'next', title: 'Next step', width: 2.0, style: { bold: true, color: GREEN } }
    ].filter((column) => notes.some((note) => note[column.key]));
    const widthSum = columns.reduce((sum, column) => sum + column.width, 0);
    const colW = columns.map((column) => (column.width / widthSum) * 12.6);
    const rowHeight = (note) => {
      const lines = columns.reduce((tallest, column, index) => {
        const words = pptText(note[column.key]).split(/\s+/).filter(Boolean);
        const capacity = Math.max(12, Math.floor(Math.max(0.4, colW[index] - 0.1) * 18));
        let count = 1;
        let used = 0;
        words.forEach((word) => {
          const need = word.length + (used ? 1 : 0);
          if (used && used + need > capacity) {
            count += 1;
            used = Math.min(word.length, capacity);
          } else {
            used += need;
          }
        });
        return Math.max(tallest, count);
      }, 1);
      return 0.1 + lines * 0.16;
    };
    const header = columns.map((column) => column.title).map(headerCell(size));
    const pages = [];
    let page = [];
    let used = 0.36;
    notes.forEach((note) => {
      const height = rowHeight(note);
      if (page.length && used + height > 6.05) {
        pages.push(page);
        page = [];
        used = 0.4;
      }
      page.push(note);
      used += height;
    });
    if (page.length) pages.push(page);
    pages.forEach((pageNotes, index) => {
      const open = pres.addSlide();
      addTitle(open, index === 0 ? 'Still open: status and next steps' : 'Still open: status and next steps (continued)');
      open.addTable([
        header,
        ...pageNotes.map((note) => columns.map((column) => cell(note[column.key], column.style)))
      ], {
        border: { type: 'solid', pt: 0.5, color: 'DDDDDD' },
        x: 0.35, y: 1.1, w: 12.6, margin: 0.05, colW
      });
    });
  }

  // 5. The problem table, as on the page
  if (rows.length) {
    const board = pres.addSlide();
    addTitle(board, active.label, 'Problems opened this month and earlier problems still open');
    const size = rows.length <= 5 ? 9 : rows.length <= 9 ? 8 : 7;
    const cell = (text, options = {}) => ({
      text: pptText(text),
      options: { fontFace: FONT, fontSize: size, color: TEXT, valign: 'top', ...options }
    });
    const stat = { bold: true, color: RED };
    const body = rows.map((row) => [
      cell(row.problemNumber, { bold: true }),
      cell(row.incidentNumbers, { bold: true }),
      cell(row.priority, { bold: true, color: GREEN }),
      cell(formatUtcDate(row.openedAt)),
      cell(formatUtcDate(row.problemClosedAt)),
      cell(row.teamName),
      cell(withUnit(row.impactHours, 'hrs'), stat),
      cell(days(row.impactDays), stat),
      cell(days(row.closedDays), stat),
      cell(row.problemShortDescription),
      cell(row.problemRca),
      cell(row.classLabel, { bold: true, color: row.major ? RED : GREEN }),
      cell(row.problemState, { bold: row.isOpen, color: row.isOpen ? 'E65100' : TEXT })
    ]);
    // Widths sum to table w (12.85) so the grid does not run past the slide edge.
    board.addTable([COLUMNS.map(headerCell(size)), ...body], {
      ...TABLE_OPTIONS, x: 0.25, y: 1.1, w: 12.85, margin: 0.03,
      colW: [0.8, 1.05, 0.7, 0.75, 0.75, 1.1, 0.75, 0.7, 0.7, 1.55, 2.6, 0.7, 0.7]
    });
    board.addText(
      `${pptText(PAGE_SCOPE_NOTE)} ${plainText(MAJOR_DEFINITION.parts)} ${pptText(PRIORITY_NOTE)} ${plainText(IMPACT_NOTE.parts)}`,
      { x: 0.25, y: 6.95, w: 12.85, h: 0.45, valign: 'top', fontFace: FONT, fontSize: 9, color: GREY }
    );
  }

  await pres.writeFile({ fileName: `Incident-Remediation-${active.key}.pptx` });
};
